import { useCallback, useState } from 'react';

import { usePostHog } from 'posthog-js/react';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

interface UseFormStateOptions<T extends Record<string, string>> {
  initialValues: T;
  validate: (values: T) => Partial<Record<keyof T, string>>;
  endpoint: string;
  /** Resets the form on each open, so a reopened modal never shows the previous result. */
  open: boolean;
  source?: string;
}

export function useFormState<T extends Record<string, string>>({
  initialValues,
  validate,
  endpoint,
  open,
  source,
}: UseFormStateOptions<T>) {
  // PostHog's React docs are explicit that the hook is the only safe accessor: importing the
  // singleton can read it before the provider initializes it.
  const posthog = usePostHog();
  const [formState, setFormState] = useState<FormState>('idle');
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [values, setValues] = useState<T>(initialValues);
  const [wasOpen, setWasOpen] = useState(open);

  if (open && !wasOpen) {
    setWasOpen(true);
    setFormState('idle');
    setErrors({});
    setValues(initialValues);
  } else if (!open && wasOpen) {
    setWasOpen(false);
  }

  const handleChange = useCallback((field: keyof T, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: undefined };
    });
  }, []);

  const clearField = useCallback((field: keyof T) => {
    setValues(prev => ({ ...prev, [field]: '' }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: undefined };
    });
  }, []);

  const handleSubmit = useCallback(
    async (e: { preventDefault: () => void }) => {
      e.preventDefault();

      const fieldErrors = validate(values);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }

      setErrors({});
      setFormState('submitting');

      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // Undefined when analytics is off (outside production the provider isn't mounted at all),
          // and the route falls back to the email in that case.
          body: JSON.stringify({ ...values, source, distinctId: posthog?.get_distinct_id?.() }),
        });

        if (!res.ok) throw new Error('Failed to submit');

        setFormState('success');
      } catch {
        setFormState('error');
      }
    },
    [values, validate, endpoint, source, posthog],
  );

  return { formState, errors, values, handleChange, clearField, handleSubmit };
}
